// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// <graphServiceSnippet>
import { Injectable } from '@angular/core';
import { Client } from '@microsoft/microsoft-graph-client';
import { AdministratorService } from 'app/administrator/administrator.service';
import { ToastrService } from 'ngx-toastr';
import * as moment from 'moment-timezone';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class GraphService {
  currentUser: any;
  isAdmin: boolean;
  isQRMAdmin: boolean;
  isDeveloper: boolean;
  isAuditTeam: boolean;
  isAuditAssistTeam: boolean;
  usersData: any;
  currentUserData: any;
  userPermission: any;
  importantDate: any;
  private notify = new Subject<any>();
  notifyObservable$ = this.notify.asObservable();
  constructor(private readonly administratorService: AdministratorService, private toastr: ToastrService) { }

  async getUsersbyFilter(filter): Promise<any> {
    try {
      var result = await this.administratorService.postData({ searchString: filter }, 'GetDimADUsers', false);
      return result;
    } catch (error) {
      this.toastr.error('Some error occured. Please try again later.', 'Error!');
      // console.log('Could not get events', JSON.stringify(error, null, 2));
    }
  }

  async getCurrentUser(): Promise<any> {
    this.currentUser = await this.administratorService.getData('GetDimADCurrentUser');

    await this.getUserPermissions();
    return this.currentUser;
  }

  async getUserPermissions() {

    if (!this.currentUser) {
      this.currentUser = await this.administratorService.getData('GetDimADCurrentUser');
    }
    if (!this.usersData) {
      this.usersData = await this.administratorService.getData('DimUsers');
    }
    const userObject = this.usersData.filter((x) => {
      return !!x && !!x.UserEmail && x.UserEmail.toLocaleLowerCase() === this.currentUser.mail.toLocaleLowerCase();
    });
    if (!!userObject) {
      this.currentUserData = userObject[0];
    }
    if (userObject && userObject.length > 0 && userObject[0].Active) {
      this.isAuditTeam = false;
      this.isAdmin = false;
      this.isQRMAdmin = false;
      this.isDeveloper = false;
      this.isAuditAssistTeam = false;
      switch (userObject[0] ? userObject[0].PermissionLevel : 'Audit team') {
        case 'Audit team':
          this.isAuditTeam = true;
          break;
        case 'Audit assist team':
          this.isAuditAssistTeam = true;
          break;
        case 'Admin':
          this.isAdmin = true;
          break;
        case 'QRM admin':
          this.isQRMAdmin = true;
          break;
        case 'Developer':
          this.isDeveloper = true;
          break;
        default:
          this.isAuditTeam = true;
      }
      this.userPermission = {
        isAdmin: this.isAdmin,
        isAuditTeam: this.isAuditTeam,
        isAuditAssistTeam: this.isAuditAssistTeam,
        isDeveloper: this.isDeveloper,
        hasAccess: true,
        isQRMAdmin: this.isQRMAdmin
      };

      this.notifyChild({ data: this.userPermission });
    } else {
      this.userPermission = {
        isAdmin: false,
        isAuditTeam: false,
        isAuditAssistTeam: false,
        isDeveloper: false,
        hasAccess: false,
        isQRMAdmin: false
      };
    }
  }

  setCurrentUserPermissions(currentUserData) {
    if (currentUserData.UserEmail === this.currentUser.mail && currentUserData.Active) {
      this.isAuditTeam = false;
      this.isAdmin = false;
      this.isQRMAdmin = false;
      this.isDeveloper = false;
      this.isAuditAssistTeam = false;
      const userObject = currentUserData;
      switch (userObject ? userObject.PermissionLevel : 'Audit team') {
        case 'Audit team':
          this.isAuditTeam = true;
          break;
        case 'Audit assist team':
          this.isAuditAssistTeam = true;
          break;
        case 'Admin':
          this.isAdmin = true;
          break;
        case 'QRM admin':
          this.isQRMAdmin = true;
          break;
        case 'Developer':
          this.isDeveloper = true;
          break;
        default:
          this.isAuditTeam = true;
      }
      this.userPermission = {
        isAdmin: this.isAdmin,
        isAuditTeam: this.isAuditTeam,
        isAuditAssistTeam: this.isAuditAssistTeam,
        isDeveloper: this.isDeveloper,
        isQRMAdmin: this.isQRMAdmin
      };

      this.notifyChild({ data: this.userPermission });
      location.reload();
    } else {
      this.userPermission = {
        isAdmin: false,
        isAuditTeam: false,
        isAuditAssistTeam: false,
        isDeveloper: false,
        hasAccess: false,
        isQRMAdmin: false
      };
    }
  }

  public notifyChild(data: any) {
    if (data) {
      if (data.component == 'ImpDate') {
        this.importantDate = data;
      }
      this.notify.next(data);
    }
  }

  async getDimUsersByFilter(filter): Promise<any> {
    try {
      var result = await this.administratorService.getDataByID('GetFilteredUser', filter);
      return result;
    } catch (error) {
      this.toastr.error('Some error occured. Please try again later.', 'Error!');
      // console.log('Could not get events', JSON.stringify(error, null, 2));
    }
  }

  async getDimUsersByUserId(userId): Promise<any> {
    try {
      var result = await this.administratorService.getDataByID('DimUserByUserId', userId);
      return result;
    } catch (error) {
      this.toastr.error('Some error occured. Please try again later.', 'Error!');
      // console.log('Could not get events', JSON.stringify(error, null, 2));
    }
  }

}
